/**
 * Utilitário completo para validação, formatação e conversão de boletos brasileiros
 * (Boletos Bancários FEBRABAN de 47 dígitos e Concessionárias/Tributos de 48 dígitos).
 */

/**
 * Limpa uma string mantendo apenas números.
 */
export function cleanDigits(value: string | null | undefined): string {
  if (!value) return '';
  return String(value).replace(/\D/g, '');
}

/**
 * Cálculo do Módulo 10 (Padrão FEBRABAN).
 */
export function modulo10(bloco: string): number {
  const digits = bloco.replace(/\D/g, '').split('').reverse();
  let soma = 0;

  for (let i = 0; i < digits.length; i++) {
    const num = parseInt(digits[i], 10);
    const multiplicador = (i % 2 === 0) ? 2 : 1;
    const multiplicacao = num * multiplicador;
    soma += Math.floor(multiplicacao / 10) + (multiplicacao % 10);
  }

  const resto = soma % 10;
  return resto === 0 ? 0 : (10 - resto);
}

/**
 * Cálculo do Módulo 11 (Padrão FEBRABAN para código de barras de boletos bancários).
 */
export function modulo11Bancario(bloco: string): number {
  const digits = bloco.replace(/\D/g, '').split('').reverse();
  let soma = 0;
  let peso = 2;

  for (let i = 0; i < digits.length; i++) {
    const num = parseInt(digits[i], 10);
    soma += num * peso;
    peso = peso === 9 ? 2 : peso + 1;
  }

  const resto = soma % 11;
  const dv = 11 - resto;
  if (dv === 0 || dv === 10 || dv === 11) {
    return 1;
  }
  return dv;
}

/**
 * Cálculo do Módulo 11 para Concessionárias / Tributos.
 */
export function modulo11Concessionaria(bloco: string): number {
  const digits = bloco.replace(/\D/g, '').split('').reverse();
  let soma = 0;
  let peso = 2;

  for (let i = 0; i < digits.length; i++) {
    const num = parseInt(digits[i], 10);
    soma += num * peso;
    peso = peso === 9 ? 2 : peso + 1;
  }

  const resto = soma % 11;
  if (resto === 0 || resto === 1) return 0;
  if (resto === 10) return 0;
  return 11 - resto;
}

/**
 * Converte Linha Digitável de 47 dígitos (Boleto Bancário) para Código de Barras de 44 dígitos.
 */
export function linhaDigitavel47ParaCodigoBarras(linha47: string): string {
  const limpo = cleanDigits(linha47);
  if (limpo.length !== 47) return limpo;

  const banco = limpo.substring(0, 3);
  const moeda = limpo.substring(3, 4);
  const campoLivre1 = limpo.substring(4, 9);
  // DV1 na pos 9 ignorado
  const campoLivre2 = limpo.substring(10, 20);
  // DV2 na pos 20 ignorado
  const campoLivre3 = limpo.substring(21, 31);
  // DV3 na pos 31 ignorado
  const k = limpo.substring(32, 33);
  const fatorValor = limpo.substring(33, 47);

  return `${banco}${moeda}${k}${fatorValor}${campoLivre1}${campoLivre2}${campoLivre3}`;
}

/**
 * Converte Código de Barras de 44 dígitos (Boleto Bancário) para Linha Digitável de 47 dígitos.
 */
export function codigoBarrasParaLinhaDigitavel47(barcode: string): string {
  const limpo = cleanDigits(barcode);
  if (limpo.length !== 44) return limpo;

  const banco = limpo.substring(0, 3);
  const moeda = limpo.substring(3, 4);
  const k = limpo.substring(4, 5);
  const fatorValor = limpo.substring(5, 19);
  const cLivre1 = limpo.substring(19, 24);
  const cLivre2 = limpo.substring(24, 34);
  const cLivre3 = limpo.substring(34, 44);

  const bloco1 = `${banco}${moeda}${cLivre1}`;
  const dv1 = modulo10(bloco1);

  const bloco2 = cLivre2;
  const dv2 = modulo10(bloco2);

  const bloco3 = cLivre3;
  const dv3 = modulo10(bloco3);

  return `${bloco1}${dv1}${bloco2}${dv2}${bloco3}${dv3}${k}${fatorValor}`;
}

/**
 * Converte Linha Digitável de 48 dígitos (Concessionária) para Código de Barras de 44 dígitos.
 */
export function linhaDigitavel48ParaCodigoBarras(linha48: string): string {
  const limpo = cleanDigits(linha48);
  if (limpo.length !== 48) return limpo;

  return `${limpo.substring(0, 11)}${limpo.substring(12, 23)}${limpo.substring(24, 35)}${limpo.substring(36, 47)}`;
}

/**
 * Converte Código de Barras de 44 dígitos (Concessionária iniciada com 8) para Linha Digitável de 48 dígitos.
 */
export function codigoBarrasParaLinhaDigitavel48(barcode: string): string {
  const limpo = cleanDigits(barcode);
  if (limpo.length !== 44 || !limpo.startsWith('8')) return limpo;

  const b1 = limpo.substring(0, 11);
  const b2 = limpo.substring(11, 22);
  const b3 = limpo.substring(22, 33);
  const b4 = limpo.substring(33, 44);

  // Verifica tipo de moeda no 3º dígito
  const tipoMoeda = limpo.charAt(2);
  const calculaDv = (tipoMoeda === '6' || tipoMoeda === '7') ? modulo10 : modulo11Concessionaria;

  const dv1 = calculaDv(b1);
  const dv2 = calculaDv(b2);
  const dv3 = calculaDv(b3);
  const dv4 = calculaDv(b4);

  return `${b1}${dv1}${b2}${dv2}${b3}${dv3}${b4}${dv4}`;
}

/**
 * Formata uma Linha Digitável para exibição legível com pontos e espaços (Padrão FEBRABAN).
 */
export function formatarLinhaDigitavel(linha: string): string {
  const limpo = cleanDigits(linha);
  if (limpo.length === 47) {
    return `${limpo.substring(0, 5)}.${limpo.substring(5, 10)} ${limpo.substring(10, 15)}.${limpo.substring(15, 21)} ${limpo.substring(21, 26)}.${limpo.substring(26, 32)} ${limpo.substring(32, 33)} ${limpo.substring(33)}`;
  }
  if (limpo.length === 48) {
    return `${limpo.substring(0, 12)} ${limpo.substring(12, 24)} ${limpo.substring(24, 36)} ${limpo.substring(36, 48)}`;
  }
  return linha;
}

/**
 * Formata Código de Barras (44 dígitos) para facilitar a leitura humana.
 */
export function formatarCodigoBarras(barcode: string): string {
  const limpo = cleanDigits(barcode);
  if (limpo.length === 44) {
    return `${limpo.substring(0, 4)} ${limpo.substring(4, 5)} ${limpo.substring(5, 9)} ${limpo.substring(9, 19)} ${limpo.substring(19, 24)} ${limpo.substring(24, 34)} ${limpo.substring(34, 44)}`;
  }
  return barcode;
}

/**
 * Valida os dígitos verificadores (DVs) de uma Linha Digitável de 47 dígitos.
 */
export function validarLinhaDigitavel47(linha: string): { valida: boolean; erro?: string } {
  const limpo = cleanDigits(linha);
  if (limpo.length !== 47) {
    return { valida: false, erro: `Tamanho inválido: ${limpo.length} dígitos (esperado 47)` };
  }

  // Bloco 1
  const b1 = limpo.substring(0, 9);
  const dv1Esperado = parseInt(limpo.charAt(9), 10);
  const dv1Calculado = modulo10(b1);
  if (dv1Calculado !== dv1Esperado) {
    return { valida: false, erro: `Dígito verificador do Campo 1 inválido (calculado: ${dv1Calculado}, lido: ${dv1Esperado})` };
  }

  // Bloco 2
  const b2 = limpo.substring(10, 20);
  const dv2Esperado = parseInt(limpo.charAt(20), 10);
  const dv2Calculado = modulo10(b2);
  if (dv2Calculado !== dv2Esperado) {
    return { valida: false, erro: `Dígito verificador do Campo 2 inválido (calculado: ${dv2Calculado}, lido: ${dv2Esperado})` };
  }

  // Bloco 3
  const b3 = limpo.substring(21, 31);
  const dv3Esperado = parseInt(limpo.charAt(31), 10);
  const dv3Calculado = modulo10(b3);
  if (dv3Calculado !== dv3Esperado) {
    return { valida: false, erro: `Dígito verificador do Campo 3 inválido (calculado: ${dv3Calculado}, lido: ${dv3Esperado})` };
  }

  // Validação geral do código de barras resultante
  const barcode = linhaDigitavel47ParaCodigoBarras(limpo);
  const kEsperado = parseInt(barcode.charAt(4), 10);
  const barcodeSemK = `${barcode.substring(0, 4)}${barcode.substring(5)}`;
  const kCalculado = modulo11Bancario(barcodeSemK);

  if (kCalculado !== kEsperado) {
    return { valida: false, erro: `Dígito geral do código de barras inválido (calculado: ${kCalculado}, lido: ${kEsperado})` };
  }

  return { valida: true };
}

/**
 * Tenta corrigir pequenos erros pontuais de OCR (como 8 vs 3, 1 vs 7, 0 vs 6/8) em uma linha digitável
 * usando a verificação estrita dos dígitos verificadores (Módulo 10 e Módulo 11).
 */
export function corrigirErrosOcrLinhaDigitavel(linhaRaw: string): string {
  let limpo = cleanDigits(linhaRaw);
  if (limpo.length !== 47 && limpo.length !== 48 && limpo.length !== 44) {
    return limpo;
  }

  if (limpo.length === 44) {
    // É código de barras, gera a linha digitável correspondente
    if (limpo.startsWith('8')) {
      return codigoBarrasParaLinhaDigitavel48(limpo);
    }
    return codigoBarrasParaLinhaDigitavel47(limpo);
  }

  if (limpo.length === 47) {
    // Se já é 100% válida, retorna
    if (validarLinhaDigitavel47(limpo).valida) {
      return limpo;
    }

    // Mapa de confusões comuns de OCR em dígitos numéricos
    const ocrConfusions: Record<string, string[]> = {
      '0': ['8', '6', '9'],
      '1': ['7', '4', '2'],
      '2': ['7', '1'],
      '3': ['8', '5'],
      '4': ['1', '9'],
      '5': ['6', '3', '8'],
      '6': ['0', '5', '8'],
      '7': ['1', '2'],
      '8': ['0', '3', '6', '9'],
      '9': ['0', '8', '4']
    };

    // Tenta substituir 1 dígito por vez nas posições suspeitas para encontrar uma linha 100% válida
    const chars = limpo.split('');
    for (let pos = 0; pos < 47; pos++) {
      const originalChar = chars[pos];
      const alternatives = ocrConfusions[originalChar] || [];

      for (const alt of alternatives) {
        chars[pos] = alt;
        const candidate = chars.join('');
        if (validarLinhaDigitavel47(candidate).valida) {
          console.log(`[BoletoUtils] Auto-corrigido erro de OCR na posição ${pos}: '${originalChar}' -> '${alt}'`);
          return candidate;
        }
      }
      chars[pos] = originalChar; // Restaura para a próxima tentativa
    }
  }

  return limpo;
}

/**
 * Processa e harmoniza qualquer código de barras ou linha digitável extraído da imagem.
 * Retorna os dois campos 100% sincronizados, formatados e validados.
 */
export function processarBoletoExtraido(rawCodigoBarras?: string, rawLinhaDigitavel?: string): {
  codigo_barras: string;
  linha_digitavel: string;
  linha_digitavel_formatada: string;
  valido: boolean;
} {
  const cdLimpo = cleanDigits(rawCodigoBarras);
  const ldLimpo = cleanDigits(rawLinhaDigitavel);

  // Determina a melhor fonte de dados (linha digitável com 47/48 dígitos é mais comum e rica)
  let fonte = '';
  if (ldLimpo.length === 47 || ldLimpo.length === 48) {
    fonte = ldLimpo;
  } else if (cdLimpo.length === 47 || cdLimpo.length === 48) {
    fonte = cdLimpo;
  } else if (cdLimpo.length === 44) {
    fonte = cdLimpo;
  } else if (ldLimpo.length === 44) {
    fonte = ldLimpo;
  } else {
    fonte = ldLimpo || cdLimpo;
  }

  // Tenta auto-correção de OCR
  const corrigido = corrigirErrosOcrLinhaDigitavel(fonte);

  let finalLinhaDigitavel = '';
  let finalCodigoBarras = '';

  if (corrigido.length === 47) {
    finalLinhaDigitavel = corrigido;
    finalCodigoBarras = linhaDigitavel47ParaCodigoBarras(corrigido);
  } else if (corrigido.length === 48) {
    finalLinhaDigitavel = corrigido;
    finalCodigoBarras = linhaDigitavel48ParaCodigoBarras(corrigido);
  } else if (corrigido.length === 44) {
    finalCodigoBarras = corrigido;
    finalLinhaDigitavel = corrigido.startsWith('8') 
      ? codigoBarrasParaLinhaDigitavel48(corrigido)
      : codigoBarrasParaLinhaDigitavel47(corrigido);
  } else {
    finalCodigoBarras = cdLimpo;
    finalLinhaDigitavel = ldLimpo;
  }

  const validacao = finalLinhaDigitavel.length === 47 
    ? validarLinhaDigitavel47(finalLinhaDigitavel).valida 
    : (finalCodigoBarras.length === 44);

  return {
    codigo_barras: finalCodigoBarras,
    linha_digitavel: finalLinhaDigitavel,
    linha_digitavel_formatada: formatarLinhaDigitavel(finalLinhaDigitavel),
    valido: validacao
  };
}
